import {OrgPatientSearchResult} from '../models/org-patient-search-result';

export interface CohortGroup {
  /** Stable key for *ngFor / @for tracking. Cohort id when grouped, otherwise the patient PK. */
  key: string;
  /** Multi-practice cohort (linkGroupId set, 2+ rows in the result) vs. singleton row. */
  kind: 'cohort' | 'singleton';
  /** All matching rows in the group. For singletons this is length 1. */
  members: OrgPatientSearchResult[];
}

/**
 * Group an unsorted list of org-search results by `linkGroupId` for display.
 *
 * - Rows with a non-null `linkGroupId` *and* 2+ matching rows in the result
 *   collapse into one `cohort` group.
 * - Rows with `linkGroupId === null`, or the only matching member of a cohort,
 *   render as a `singleton`.
 *
 * Original ordering of the first occurrence of each group is preserved (so the
 * list still respects backend-side relevance via `queryWeight`).
 */
export function groupSearchResultsByCohort(items: OrgPatientSearchResult[]): CohortGroup[] {
  const groupOrder: string[] = [];
  const groupsByKey = new Map<string, OrgPatientSearchResult[]>();

  for (const item of items) {
    const key = item.linkGroupId ?? `singleton:${item.id}`;
    if (!groupsByKey.has(key)) {
      groupsByKey.set(key, []);
      groupOrder.push(key);
    }
    groupsByKey.get(key)!.push(item);
  }

  return groupOrder.map(key => {
    const members = groupsByKey.get(key)!;
    const isMultiPracticeCohort = members.length > 1 && members[0].linkGroupId !== null;
    return {
      key,
      kind: isMultiPracticeCohort ? 'cohort' : 'singleton',
      members,
    };
  });
}
